import { IS_TAB_DICTATION_ENABLED } from '@/app-shell/flags/dictate-flags';
import { isDictationEnabled } from '@/composition/hosts/dictation/hooks/useIsDictationEnabled';
import { isUniversalDictationApiAvailable } from '@/utils/backendSurfaces';
import { isDictationAvailable } from '@/utils/dictateRecorderTransport';
import { TypographyP2 } from '@heidi/design-system/typography';
import { toggleUniversalDictationFromEditor } from '@heidi/dictate';
import { getCurrentSessionId } from '@heidi/runtime/shell';
import { DictationCleanupModes } from '@heidi/schema/ml-scribe';
import { type Editor } from '@tiptap/react';
import { Mic, Sparkles } from 'lucide-react';
import { type Command, type Group } from './types';

/**
 * Shared dictation slash-command building blocks for the tiptap editors
 * (v1, vJSON, vMarkdown). Extracted to remove the duplicated dictation group
 * logic the three editors previously each carried.
 */

/**
 * Whether the dictation slash command should be offered at all. The chunked
 * transport is session-scoped, so an editor outside a session (template
 * builder) has nothing to dictate against there.
 */
export const isDictationCommandEnabled = (): boolean =>
  isDictationAvailable(getCurrentSessionId());

/**
 * Whether the universal-dictation bubble is both switched on and backed by a
 * server this build can reach. Everything else falls to the note-editor path.
 */
const isUniversalDictationActive = (): boolean =>
  isUniversalDictationApiAvailable && isDictationEnabled();

/**
 * The `/dictation` command. The universal-vs-legacy routing is evaluated inside
 * the action (i.e. at click time, not menu-open time) so a feature-flag flip
 * between opening the slash menu and selecting the command is honoured.
 */
export const createDictationCommand = (editor: Editor): Command => ({
  name: 'dictation',
  label: 'Dictation',
  icon: Mic,
  action: () => {
    if (isUniversalDictationActive()) {
      toggleUniversalDictationFromEditor(editor);
      return;
    }
    editor.commands.onClickFromSlashCommand({
      shouldShowCursor: true,
      shouldInformTab: true,
    });
  },
  content: <TypographyP2>Dictation</TypographyP2>,
});

/**
 * The legacy "Smart Dictation" command (AI cleanup). Only offered on the legacy
 * dictation path — universal dictation has no smart variant.
 */
const createSmartDictationCommand = (editor: Editor): Command => ({
  name: 'smartDictation',
  label: 'Smart Dictation',
  icon: Sparkles,
  action: () => {
    editor.commands.onClickFromSlashCommand({
      shouldShowCursor: true,
      shouldInformTab: true,
      dictationMode: DictationCleanupModes.SMART,
    });
  },
  content: <TypographyP2>Smart Dictation</TypographyP2>,
});

/**
 * The standalone "DICTATION" slash-command group used by the vJSON and vMarkdown
 * editors. Returns `null` when dictation is disabled so callers can skip pushing
 * it. Smart dictation is appended only on the legacy path (it is hidden under
 * universal dictation).
 */
export const createDictationGroup = (editor: Editor): Group | null => {
  if (!isDictationCommandEnabled()) return null;

  const showSmartDictation = !isUniversalDictationActive() && IS_TAB_DICTATION_ENABLED();

  return {
    name: 'format',
    title: 'DICTATION',
    commands: [
      createDictationCommand(editor),
      ...(showSmartDictation ? [createSmartDictationCommand(editor)] : []),
    ],
  };
};
